import { Autocomplete, CircularProgress, TextField, createFilterOptions } from "@mui/material";
import { useEffect, useState } from "react";

export interface AutoCompleteOptionType {
  inputValue?: string;
  title: string;
  id?: string;
}

interface AutoCompleteProps {
  value: AutoCompleteOptionType | null;
  onChange: (newValue: AutoCompleteOptionType | null, isNew?: boolean) => void;
  options: AutoCompleteOptionType[];
  placeholder: string;
  loading: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  validate: (value: string) => string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  onSearch: (value: string) => void;
}

const filter = createFilterOptions<AutoCompleteOptionType>();

export default function AutoComplete({
  value,
  onChange,
  validate,
  setError,
  options = [],
  placeholder,
  loading,
  onKeyDown,
  onSearch,
}: AutoCompleteProps) {
  const [hideOptions, setHideOptions] = useState(true);

  const handleChange = (_event: React.SyntheticEvent, newValue: AutoCompleteOptionType | string | null) => {
    if (typeof newValue === "string") {
      onChange({ title: newValue });
    } else if (newValue?.inputValue) {
      onChange({ title: newValue.inputValue }, true);
    } else {
      onChange(newValue);
    }
  };

  const filterOptions = (options: AutoCompleteOptionType[], params: any) => {
    const filtered = filter(options, params);
    const { inputValue } = params;

    const isExisting = options.some((option) => inputValue.toLowerCase() === option.title.toLowerCase());

    if (inputValue && !isExisting) {
      filtered.push({
        inputValue,
        title: `Add "${inputValue}"`,
      });
    }

    return filtered;
  };

  const getOptionLabel = (option: AutoCompleteOptionType | string) => {
    if (typeof option === "string") return option;
    return option.inputValue ? option.inputValue : option.title;
  };

  return (
    <Autocomplete
      value={value}
      options={options.length > 0 ? options : [{ title: "No options", id: "no-options" }]}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      loading={loading}
      selectOnFocus
      handleHomeEndKeys
      onBlur={() => {
        onSearch(value?.title ?? "");
      }}
      renderOption={(props, option) => (
        <li {...props} style={{ opacity: option?.id === "no-options" ? 0.5 : 1 }}>
          {option.title}
        </li>
      )}
      onInputChange={(_event, value) => {
        setHideOptions(value.length <= 2);
        setError(validate(value));

        if (value.length > 2) {
          onSearch(value);
        }
      }}
      componentsProps={{
        popper: {
          sx: {
            "& .MuiAutocomplete-noOptions": {
              display: "none",
            },
            "& .MuiAutocomplete-paper": {
              display: (loading || hideOptions) ? "none" : undefined,
            },
            "& .MuiAutocomplete-paper .MuiAutocomplete-listbox": {
              padding: 0,
              overflowX: "scroll",
            },
            "& .MuiAutocomplete-paper .MuiAutocomplete-listbox .MuiAutocomplete-option": {
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }
          },
        },
      }}
      sx={{
        ".MuiOutlinedInput-root": {
          paddingRight: "45px !important",
          fontWeight: 500,
          fontFamily: "var(--font-inter)",
          fontSize: "var(--font-size-xs)",
          backgroundColor: "var(--color-whitesmoke-400)",
          borderRadius: "var(--br-3xs)",
          height: 40,
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
        },
        ".MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        ".MuiAutocomplete-popupIndicator": {
          display: "none",
        },
        width: "100%",
        Padding: 0,
        ".MuiPopper-root .MuiAutocomplete-noOptions": {
          display: "none !important",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          onKeyDown={onKeyDown}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      getOptionDisabled={(option) => {
        return option?.id === "no-options";
      }}
    />
  );
}
